import { useCallback, useEffect, useMemo, useState } from "react";
import { getStoredSelectedAppId, setStoredSelectedAppId } from "@/lib/session";
import {
  SelectedAppContext,
  type SelectedAppContextValue,
} from "@/lib/selectedAppContextValue";
import { useAuthContext } from "@/lib/useAuthContext";

/**
 * SelectedAppProvider — provider component for the selected-app +
 * session context.
 *
 * Sources ``workspaceId`` / ``userId`` / ``userDisplayName`` from the
 * authenticated session (``AuthContext``) instead of URL query params
 * + localStorage. Mounted under ``ProtectedRoute`` so the session is
 * always available when this provider runs (App.tsx wires the gate).
 *
 * The selected-app id remains a localStorage-backed preference — it's
 * a per-device UX state, not authentication.
 *
 * Source: ENG-AUTH-LOGIN-SIGNUP supersedes the BRIEF_ia-redesign-frontend §6
 * "URL query params at MVP" placeholder.
 */

export function SelectedAppProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { session } = useAuthContext();
  const workspaceId = session?.workspace?.workspace_id ?? null;
  const userId = session?.user?.user_id ?? null;
  const userDisplayName =
    session?.user?.display_name ?? session?.user?.email ?? null;
  const [selectedAppId, setSelectedAppIdState] = useState<string | null>(() =>
    getStoredSelectedAppId(),
  );

  // Persist selectedAppId to localStorage whenever it changes.
  useEffect(() => {
    setStoredSelectedAppId(selectedAppId);
  }, [selectedAppId]);

  // Global locale (ASOLOOP-UX item 1). `null` = "All locales" — the default,
  // and deliberately NOT persisted: the locale universe is per-app (it comes
  // from that app's discovered/target locales), so a code remembered from
  // another app would silently filter everything to nothing.
  const [selectedLocale, setSelectedLocaleState] = useState<string | null>(
    null,
  );

  const setSelectedAppId = useCallback((next: string | null) => {
    setSelectedAppIdState(next);
  }, []);

  // Switching apps resets to "All locales" — matches the existing rule that
  // filter selections reset on app switch, and prevents a stale locale from an
  // unrelated app blanking every surface. Adjusted DURING RENDER (React's
  // documented "derive state from a change" pattern) rather than in an effect:
  // a synchronous setState inside useEffect triggers a cascading re-render and
  // would flash one frame of the previous app's locale filter.
  const [localeOwnerAppId, setLocaleOwnerAppId] = useState<string | null>(
    selectedAppId,
  );
  if (localeOwnerAppId !== selectedAppId) {
    setLocaleOwnerAppId(selectedAppId);
    setSelectedLocaleState(null);
  }

  const setSelectedLocale = useCallback((next: string | null) => {
    setSelectedLocaleState(next);
  }, []);

  const value = useMemo<SelectedAppContextValue>(
    () => ({
      workspaceId,
      userId,
      userDisplayName,
      selectedAppId,
      setSelectedAppId,
      selectedLocale,
      setSelectedLocale,
    }),
    [
      workspaceId,
      userId,
      userDisplayName,
      selectedAppId,
      setSelectedAppId,
      selectedLocale,
      setSelectedLocale,
    ],
  );

  return (
    <SelectedAppContext.Provider value={value}>
      {children}
    </SelectedAppContext.Provider>
  );
}
